import { Link } from "@inertiajs/react"
import { Trans, useLingui } from "@lingui/react/macro"
import type {
  DealDrawer as DealDrawerData,
  DealDrawerConversation,
} from "@/types/v2/conversation"
import type { DealPropKey } from "@/lib/deal-prop-update"
import { useDealConversations } from "@/hooks/use-deal-conversations"

// CONVERSATIONS section rendered in the right rail of the
// desktop deal slideout (`DealDrawer`), the deal-index
// slideover, and the full deal show page (`DealShowPanel`),
// plus the mobile deal show panel (CON-261).
//
// Mirrors `DealContactCard`'s structure + visual language:
// the same cream card, the same section header with a count
// badge + `SectionLabel`. Each row reads like an inbox row —
// avatar + contact name + last-message preview + "needs
// reply" dot — and opens the thread in the inbox on click.
// Unlinkable rows carry an "×" detach affordance that sits as
// a SIBLING of the row `<Link>` so the click doesn't bubble
// into navigation.
//
// The section renders only when there is ≥1 conversation:
// every deal has at least its contact's thread in practice,
// so a zero count means orphaned data — hide the section.

interface DealConversationsCardProps {
  deal: DealDrawerData
  dealPropKey: DealPropKey
}

export function DealConversationsCard({
  deal,
  dealPropKey,
}: DealConversationsCardProps) {
  const conversations = deal.conversations ?? []
  const { unlink, submitting } = useDealConversations({
    deal,
    dealPropKey,
  })

  if (conversations.length === 0) return null

  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <SectionLabel>
          <Trans>Conversations</Trans>
        </SectionLabel>
        <span className="flex items-center justify-center rounded-full bg-[#F0EDE6] text-[#7A746B] text-[10px] leading-3 font-medium px-1.5 min-w-[18px] h-[18px]">
          {conversations.length}
        </span>
      </div>

      <div className="flex flex-col rounded-xl bg-card border border-[#E5DFD3] divide-y divide-[#F0EDE6]">
        {conversations.map((conversation) => (
          <ConversationRow
            key={conversation.id}
            conversation={conversation}
            onUnlink={() => unlink(conversation.id)}
            submitting={submitting}
          />
        ))}
      </div>
    </section>
  )
}

// ── Individual conversation row ─────────────────────────

interface ConversationRowProps {
  conversation: DealDrawerConversation
  onUnlink: () => void
  submitting: boolean
}

function ConversationRow({
  conversation,
  onUnlink,
  submitting,
}: ConversationRowProps) {
  const { t } = useLingui()
  const name = conversation.contactName ?? t`Unknown contact`

  return (
    <div
      data-testid={`deal-conversation-row-${conversation.id}`}
      className="group/conversation flex items-center py-3 px-4 gap-3 min-w-0"
    >
      <Link
        href={conversation.href}
        data-testid={`deal-conversation-link-${conversation.id}`}
        className="flex items-center grow gap-3 min-w-0 text-left rounded-lg -my-1 -mx-1 px-1 py-1 hover:bg-[#FAF6EE]"
      >
        <span className="shrink-0 flex items-center justify-center rounded-full bg-[#E5DFD3] size-9">
          <span className="font-semibold text-foreground text-[11px] leading-3.5">
            {conversation.initials ?? "?"}
          </span>
        </span>

        <div className="grow min-w-0 flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-semibold text-foreground text-sm leading-[18px] truncate min-w-0">
              {name}
            </span>
            {conversation.needsReply ? (
              <span
                data-testid={`deal-conversation-needs-reply-${conversation.id}`}
                aria-label={t`Needs reply`}
                className="shrink-0 size-1.5 rounded-full bg-green-500"
              />
            ) : null}
            {conversation.source ? (
              <span
                data-testid={`deal-conversation-source-chip-${conversation.id}`}
                className="shrink-0 text-[9px] leading-3 font-semibold text-[#7A746B] bg-[#F0EDE6] px-1.5 py-0.5 rounded-full"
              >
                <Trans>Source</Trans>
              </span>
            ) : null}
          </div>

          {conversation.preview ? (
            <div className="text-[#7A746B] text-xs leading-4 truncate min-w-0">
              {conversation.preview}
            </div>
          ) : null}
        </div>

        {conversation.lastActiveLabel ? (
          <span className="shrink-0 text-[#9A938A] text-[11px] leading-[14px]">
            {conversation.lastActiveLabel}
          </span>
        ) : null}
      </Link>

      {conversation.unlinkable ? (
        <button
          type="button"
          onClick={onUnlink}
          disabled={submitting}
          data-testid={`deal-conversation-unlink-${conversation.id}`}
          aria-label={t`Unlink conversation`}
          className="shrink-0 flex items-center justify-center rounded-full size-7 text-[#9A938A] enabled:cursor-pointer enabled:hover:bg-[#F0EDE6] enabled:hover:text-[#5C544A] disabled:opacity-40 disabled:cursor-not-allowed opacity-0 group-hover/conversation:opacity-100 focus-visible:opacity-100 max-md:opacity-100"
        >
          <UnlinkIcon />
        </button>
      ) : null}
    </div>
  )
}

// ── Icons ───────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="tracking-[0.08em] uppercase font-medium text-[#9A938A] text-[11px] leading-[14px]">
      {children}
    </div>
  )
}

function UnlinkIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 4L12 12" />
      <path d="M12 4L4 12" />
    </svg>
  )
}
